import React from 'react';
import {
  MarketingLayout,
  Section,
  Hero,
  CallToAction,
} from '@byscott-io/corebyscott';

export default function AboutPage() {
  return (
    <MarketingLayout>
      <Hero
        headline="About BillingByScott"
        subheadline="Building practical billing solutions for real-world SaaS businesses"
        size="small"
      />

      <Section>
        <div className="max-w-3xl mx-auto">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Our Story</h2>
          <div className="prose prose-lg text-gray-600">
            <p>
              BillingByScott was born out of frustration. After building multiple SaaS
              applications, we found ourselves repeatedly implementing the same billing
              logic: subscription management, entitlement checks, usage tracking,
              payment integration. Each time, we&apos;d start from scratch.
            </p>
            <p className="mt-4">
              We knew there had to be a better way. Existing solutions were either too
              complex, too expensive, or didn&apos;t fit our multi-app architecture. So we
              built BillingByScott - a centralized billing platform that manages
              subscriptions across all our applications.
            </p>
            <p className="mt-4">
              Today, BillingByScott powers billing for the entire IdeasByScott product
              suite, and we&apos;re excited to share it with other developers facing the
              same challenges we did.
            </p>
          </div>
        </div>
      </Section>

      <Section background="gray">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">Our Values</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="text-4xl mb-4">🎯</div>
              <h3 className="text-xl font-semibold mb-2">Simplicity</h3>
              <p className="text-gray-600">
                Billing shouldn&apos;t be complicated. We build tools that are easy to
                understand and integrate.
              </p>
            </div>
            <div className="text-center">
              <div className="text-4xl mb-4">🔧</div>
              <h3 className="text-xl font-semibold mb-2">Practicality</h3>
              <p className="text-gray-600">
                We build features we actually use. No bloat, no theoretical nonsense -
                just practical solutions.
              </p>
            </div>
            <div className="text-center">
              <div className="text-4xl mb-4">🤝</div>
              <h3 className="text-xl font-semibold mb-2">Transparency</h3>
              <p className="text-gray-600">
                Clear pricing, honest documentation, and straightforward communication.
                No surprises.
              </p>
            </div>
          </div>
        </div>
      </Section>

      <Section>
        <div className="max-w-3xl mx-auto">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">Part of IdeasByScott</h2>
          <p className="text-gray-600 text-center mb-8">
            BillingByScott is part of the IdeasByScott product family - a suite of
            practical tools built to solve real problems.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-white p-6 rounded-lg shadow border">
              <div className="flex items-center gap-3 mb-3">
                <span className="text-2xl">⛪</span>
                <h3 className="text-lg font-semibold">ChurchCare</h3>
              </div>
              <p className="text-gray-600 text-sm">
                Pastoral care management for churches and religious organizations.
              </p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow border">
              <div className="flex items-center gap-3 mb-3">
                <span className="text-2xl">🎫</span>
                <h3 className="text-lg font-semibold">IssuesByScott</h3>
              </div>
              <p className="text-gray-600 text-sm">
                Simple, embeddable issue tracking for any application.
              </p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow border">
              <div className="flex items-center gap-3 mb-3">
                <span className="text-2xl">💵</span>
                <h3 className="text-lg font-semibold">FinanceByScott</h3>
              </div>
              <p className="text-gray-600 text-sm">
                Personal finance management with account aggregation.
              </p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow border">
              <div className="flex items-center gap-3 mb-3">
                <span className="text-2xl">🔐</span>
                <h3 className="text-lg font-semibold">CoreByScott</h3>
              </div>
              <p className="text-gray-600 text-sm">
                Authentication and multi-tenancy infrastructure for all our apps.
              </p>
            </div>
          </div>
        </div>
      </Section>

      <CallToAction
        headline="Ready to simplify your billing?"
        subheadline="Join the IdeasByScott family and focus on building great products."
        primaryCta={{ label: 'Get Started', href: '/register' }}
        secondaryCta={{ label: 'Contact Us', href: '/contact' }}
        background="indigo"
      />
    </MarketingLayout>
  );
}
